"use client"

import { MiniJobCard, toast } from "@/components"
import { FilterItem } from "@/components/Filters/FilterItem"
import { FilterWrapper } from "@/components/Filters/FilterWrapper"
import { Button, Separator, Spinner, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import {
  ApplyToJobContentJobPostingFragmentDoc,
  FetchMoreApplicationSelectProjectsDocument,
  GetFiltersForProfileDocument,
  JobApplicationProjectPreviewFragmentDoc,
  MyApplicationSelectProjectsFragmentDoc,
} from "@/gql/graphql"
import { useHandleListEndReached } from "@/hooks/useHandleListEndReached"
import { useQuery } from "@apollo/client"
import { useRef, useState } from "react"
import { ApplyToJobContentSkeleton } from "../ApplyToJob/ApplyToJobContentSkeleton"
import { ProjectSelectionCard } from "./ProjectSelectionCard"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { DocumentIcon } from "@heroicons/react/24/outline"
import Link from "next/link"
import { useSession } from "@/hooks/useSession"

graphql(`
  query FetchMoreApplicationSelectProjects(
    $id: ID!
    $first: Int!
    $after: String
    $category: [ID!]
    $creatorRoles: [ID!]
  ) {
    individualCreative(id: $id) {
      id
      ...MyApplicationSelectProjects
    }
  }
`)

graphql(`
  query GetFiltersForProfile($id: ID!) {
    node(id: $id) {
      ... on IndividualCreative {
        creativeWorkPortfolioProjects {
          roleCounts {
            category {
              id
              name
            }
            count
          }
          typeCounts {
            category {
              id
              name
            }
            count
          }
        }
      }
    }
  }
`)

graphql(`
  fragment MyApplicationSelectProjects on IndividualCreative {
    id
    projects: creativeWorkPortfolioProjects(
      first: $first
      after: $after
      filters: {
        status: { exact: PUBLISHED }
        category: { inList: $category }
        creatorRoles: { inList: $creatorRoles }
      }
      order: { profileOrder: ASC }
    ) {
      totalCount
      edges {
        node {
          id
          __typename
          ...JobApplicationProjectPreview
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

interface Props {
  onClose?: () => void
  loading: boolean
  creativeId: string | undefined | null
  jobPosting: FragmentType<typeof ApplyToJobContentJobPostingFragmentDoc> | undefined | null
  creativeWorkAttachments: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>[]
  setCreativeWorkAttachments: React.Dispatch<
    React.SetStateAction<FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>[]>
  >
}

export const ApplyToJobSelectWork: React.FC<Props> = ({
  loading: originalLoading,
  creativeWorkAttachments,
  setCreativeWorkAttachments,
  creativeId,
  ...props
}) => {
  const [filteredRoles, setFilteredRoles] = useState<{ id: string; name: string }[]>([])
  const [filteredProjectTypes, setFilteredProjectTypes] = useState<{ id: string; name: string }[]>([])
  const { data: session } = useSession()

  const jobPosting = useFragment(ApplyToJobContentJobPostingFragmentDoc, props.jobPosting)
  //const creativeOriginal = useFragment(MyApplicationSelectProjectsFragmentDoc, props.creative)

  const {
    data: projectsData,
    fetchMore,
    networkStatus,
    loading: projectsLoading,
  } = useQuery(FetchMoreApplicationSelectProjectsDocument, {
    variables: {
      id: `${creativeId}`,
      first: 8,
      after: null,
      category: filteredProjectTypes.map(type => type.id).length > 0 ? filteredProjectTypes.map(type => type.id) : null,
      creatorRoles: filteredRoles.map(role => role.id).length > 0 ? filteredRoles.map(role => role.id) : null,
    },
    skip: !creativeId,
    context: {
      version: 2,
    },
    fetchPolicy: "network-only",
  })

  const { data: filtersData } = useQuery(GetFiltersForProfileDocument, {
    variables: {
      id: `${creativeId}`,
    },
    skip: !creativeId,
  })

  const projects = getFragmentData(MyApplicationSelectProjectsFragmentDoc, projectsData?.individualCreative)?.projects

  const observerRef = useRef(null)
  useHandleListEndReached({
    fetchMore,
    pageInfo: getFragmentData(MyApplicationSelectProjectsFragmentDoc, projectsData?.individualCreative)?.projects
      .pageInfo,
    first: 8,
    observerRef: observerRef,
    networkStatus: networkStatus,
  })

  const toggleProject = (project: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>) => {
    if (
      creativeWorkAttachments.some(
        pr =>
          getFragmentData(JobApplicationProjectPreviewFragmentDoc, pr)?.id ===
          getFragmentData(JobApplicationProjectPreviewFragmentDoc, project)?.id
      )
    ) {
      setCreativeWorkAttachments(prev =>
        prev.filter(
          p =>
            getFragmentData(JobApplicationProjectPreviewFragmentDoc, p)?.id !==
            getFragmentData(JobApplicationProjectPreviewFragmentDoc, project)?.id
        )
      )
    } else {
      if (creativeWorkAttachments.length < 6) {
        setCreativeWorkAttachments(prev => [...prev, project])
      } else {
        toast.error("You can only select up to 6 projects")
      }
    }
  }

  const toggleSelectedRole = (role: { id: string; name: string }) => {
    if (filteredRoles.some(r => r.id === role.id)) {
      setFilteredRoles(prev => prev.filter(r => r.id !== role.id))
    } else {
      setFilteredRoles(prev => [...prev, role])
    }
  }

  const toggleSelectedProjectType = (projectType: { id: string; name: string }) => {
    if (filteredProjectTypes.some(r => r.id === projectType.id)) {
      setFilteredProjectTypes(prev => prev.filter(r => r.id !== projectType.id))
    } else {
      setFilteredProjectTypes(prev => [...prev, projectType])
    }
  }

  const filtersCreative = filtersData?.node?.__typename === "IndividualCreative" ? filtersData?.node : undefined

  if (originalLoading) {
    return <ApplyToJobContentSkeleton />
  }

  return (
    <div className="mt-6 flex flex-col px-5 md:px-0">
      <MiniJobCard jobPosting={jobPosting} />
      <div className="py-6">
        <Separator />
      </div>

      <Text variant="body2" className="text-fg-2">
        Choose up to 6 relevant projects for this job.
      </Text>
      <div className="mb-5 mt-6 flex flex-row gap-x-2">
        <FilterWrapper
          withHeaderSpaceOnMobile={false}
          hasSearch={false}
          onRemoveAll={() => setFilteredRoles([])}
          id="filter-roles"
          buttonText={`Roles ${filteredRoles.length > 0 ? `(${filteredRoles.length})` : ""}`}
          filterActive={filteredRoles.length > 0}
          menuPosition="left"
          footerText={`${filteredRoles.length} roles selected`}>
          <div className="mt-3 flex w-full">
            <div className="flex w-full flex-col gap-1">
              {filtersCreative?.creativeWorkPortfolioProjects.roleCounts.map(value => {
                return (
                  <FilterItem
                    key={value?.category?.id}
                    name={`${value?.category?.name} ${value?.count}` || ""}
                    selected={filteredRoles.some(r => r.id === value?.category?.id || "")}
                    onClick={() => {
                      if (value?.category?.id) {
                        toggleSelectedRole(value.category)
                      }
                    }}
                  />
                )
              })}
            </div>
          </div>
        </FilterWrapper>
        <FilterWrapper
          withHeaderSpaceOnMobile={false}
          hasSearch={false}
          onRemoveAll={() => setFilteredProjectTypes([])}
          id="filter-project-types"
          buttonText={`Project Types ${filteredProjectTypes.length > 0 ? `(${filteredProjectTypes.length})` : ""}`}
          filterActive={filteredProjectTypes.length > 0}
          menuPosition="left"
          footerText={`${filteredProjectTypes.length} project types selected`}>
          <div className="mt-3 flex w-full">
            <div className="flex w-full flex-col gap-1">
              {filtersCreative?.creativeWorkPortfolioProjects.typeCounts.map(value => {
                if (!value?.category?.id) {
                  return null
                }
                return (
                  <FilterItem
                    key={value?.category?.id}
                    name={`${value?.category?.name} ${value?.count}` || ""}
                    selected={filteredProjectTypes.some(r => r.id === value?.category?.id || "")}
                    onClick={() => {
                      if (value?.category?.id) {
                        toggleSelectedProjectType(value.category)
                      }
                    }}
                  />
                )
              })}
            </div>
          </div>
        </FilterWrapper>
      </div>
      {projects?.edges?.length && projects?.edges?.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {projects?.edges.map((project, index) => {
            return (
              <ProjectSelectionCard
                key={index}
                projectData={project?.node}
                creativeWorkAttachments={creativeWorkAttachments}
                toggleProject={toggleProject}
                positionId={jobPosting?.position?.id}
              />
            )
          })}
        </div>
      ) : (
        !projectsLoading && (
          <EmptyState
            title="No projects found"
            Icon={DocumentIcon}
            description={`${filteredRoles.length > 0 || filteredProjectTypes.length > 0 ? "No projects found" : ""} ${filteredRoles.length > 0 ? `where the role was ${filteredRoles.map(role => role.name).join("/")}` : ""} ${filteredProjectTypes.length > 0 ? `on a project type of ${filteredProjectTypes.map(type => type.name).join("/")}` : ""}`}>
            <Link className="mt-4" href={`/${session?.user.creative.handle}/projects/form`}>
              <Button>Upload project</Button>
            </Link>
          </EmptyState>
        )
      )}
      {projects?.pageInfo.hasNextPage ? (
        <div
          className="relative mb-1 mt-2 flex h-[20px] w-full flex-row items-center justify-center sm:col-span-2"
          ref={observerRef}>
          <Spinner className="bg-fg-1" />
        </div>
      ) : null}
    </div>
  )
}
